import { ADMIN_API_BASE_URL } from '../config'
import { handleApiResponse } from '../utils/commonfunction'
import {
  makeApiRequest,
  addAppTypeToParams,
  addAppTypeToBody,
} from '../utils/apiHelper'

export interface Stock {
  id: string
  symbol: string
  company_name: string
  sector?: string
  industry?: string
  market_cap?: number
  current_price?: number
  exchange: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface StockUniverse {
  id: string
  name: string
  description?: string
  stock_ids: string[]
  is_active: boolean
  created_at: string
  updated_at: string
  stock_count: number
}

export interface StockListResponse {
  stocks: Stock[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface StockUniverseListResponse {
  universes: StockUniverse[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

class StockService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('adminToken')
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  // Stock endpoints
  async getStocks(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    exchange?: string,
    sector?: string,
    active?: boolean
  ): Promise<StockListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (search) params.append('search', search)
    if (exchange) params.append('exchange', exchange)
    if (sector) params.append('sector', sector)
    if (active !== undefined) params.append('active', active.toString())

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(`/stocks?${paramsWithAppType}`, {
      headers: this.getAuthHeaders(),
    })
  }

  async createStock(stockData: Partial<Stock>): Promise<Stock> {
    const bodyWithAppType = addAppTypeToBody(stockData)
    return makeApiRequest('/stocks', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async updateStock(
    stockId: string,
    stockData: Partial<Stock>
  ): Promise<Stock> {
    const bodyWithAppType = addAppTypeToBody(stockData)
    return makeApiRequest(`/stocks/${stockId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async deleteStock(stockId: string): Promise<void> {
    return makeApiRequest(`/stocks/${stockId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
  }

  async bulkCreateStocks(stocksData: Stock[]): Promise<any> {
    const bodyWithAppType = addAppTypeToBody(stocksData)
    return makeApiRequest('/stocks/bulk-create', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  // Stock Universe endpoints
  async getStockUniverses(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    active?: boolean
  ): Promise<StockUniverseListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (search) params.append('search', search)
    if (active !== undefined) params.append('is_active', active.toString())

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(`/stock-universes?${paramsWithAppType}`, {
      headers: this.getAuthHeaders(),
    })
  }

  async getStockById(stockId: number): Promise<StockUniverse> {
    return makeApiRequest(`/stock-universes/${stockId}`, {
      headers: this.getAuthHeaders(),
    })
  }

  async getActiveStockUniverses(): Promise<{ universes: StockUniverse[] }> {
    return makeApiRequest('/stock-universes', {
      headers: this.getAuthHeaders(),
    })
  }

  async createStockUniverse(
    universeData: Partial<StockUniverse>
  ): Promise<StockUniverse> {
    const bodyWithAppType = addAppTypeToBody(universeData)
    return makeApiRequest('/stock-universes', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async updateStockUniverse(
    universeId: string,
    universeData: Partial<StockUniverse>
  ): Promise<StockUniverse> {
    const bodyWithAppType = addAppTypeToBody(universeData)
    return makeApiRequest(`/stock-universes/${universeId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async deleteStockUniverse(universeId: string): Promise<void> {
    return makeApiRequest(`/stock-universes/${universeId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
  }
}

export const stockService = new StockService()
